import {
  memo,
  useEffect,
  useRef,
  useState,
  MouseEvent,
  useCallback,
} from "react";
import { FolderPlus, FolderX, Loader } from "lucide-react";
import { Button } from "primereact/button";
import Input from "@/components/common/Input";
import SearchInput from "@/components/common/SearchInput";
import CategoryNode from "./CategoryNode";
import CloneDestDialog from "./CloneDestDialog";
import Uploader from "../../Uploader";
import { ClipArtCategory } from "@/interfaces/clip-art.interface";
import clipArtCategoryApi from "@/api/clip-art-category";
import { uploadManyClipArts } from "@/utils/clip-art";
import RootCategoryNode from "./RootCategoryNode";
import InfiniteScroll from "@/components/common/InfiniteScroll";
import { useToastStore } from "@/stores/toast.store";

const rootCategory: ClipArtCategory = {
  _id: "",
  ancestorIds: [],
  name: "__root__",
  createdAt: "",
  updateDAt: "",
};

interface Props {
  onChange?: (category?: ClipArtCategory) => void;
  onSelect?: (category?: ClipArtCategory) => void;
  disableClone?: boolean;
}

function CategoryTree({ onChange, disableClone }: Props) {
  const addToast = useToastStore((state) => state.addToast);
  const [categoryList, setCategoryList] = useState<ClipArtCategory[]>([]);
  const [creating, setCreating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<ClipArtCategory>();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const treeRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [size] = useState(20);
  const [hasMore, setHasMore] = useState(true);

  // Node registry — flat map of all mounted CategoryNode data objects, keyed by _id
  const nodeRegistry = useRef<Map<string, ClipArtCategory>>(new Map());
  const registerNode = useCallback((category: ClipArtCategory) => {
    nodeRegistry.current.set(category._id, category);
  }, []);
  const unregisterNode = useCallback((id: string) => {
    nodeRegistry.current.delete(id);
  }, []);

  // Clone state
  const [cloningSource, setCloningSource] = useState<ClipArtCategory | null>(
    null,
  );
  const [cloning, setCloning] = useState(false);

  const getCategoryList = useCallback(() => {
    clipArtCategoryApi
      .search({ sort: "-createdAt", page, size, search })
      .then((data) => {
        const categoryList = data.data;
        if (categoryList.length < size) setHasMore(false);
        setCategoryList((pre) =>
          page === 1 ? categoryList : [...pre, ...categoryList],
        );
      })
      .catch(() => setHasMore(false))
      .finally(() => setLoading(false));
  }, [search, page, size]);

  useEffect(() => {
    const timer = setTimeout(() => {
      getCategoryList();
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [getCategoryList]);

  useEffect(() => {
    if (!selectedCategory || selectedCategory._id === rootCategory._id) return;
    const existing = categoryList.some((c) =>
      [selectedCategory._id, ...selectedCategory.ancestorIds].includes(c._id),
    );
    if (!existing) {
      setSelectedCategory(undefined);
      onChange?.(undefined);
    }
  }, [selectedCategory, categoryList, onChange]);

  const onCreate = (name: string) => {
    setCreating(false);
    if (!name) return;
    clipArtCategoryApi
      .createOne({ name })
      .then((data) => {
        setCategoryList((pre) => [data.data, ...(pre || [])]);
      })
      .catch(() => {});
  };

  const onSelect = (category?: ClipArtCategory) => {
    setSelectedCategory(category);
    onChange?.(category);
  };

  const onDelete = (category: ClipArtCategory) => {
    clipArtCategoryApi
      .deleteOneById(category._id)
      .then((data) => {
        const deletedCategory = data.data;
        setCategoryList((state) =>
          state.filter((c) => c._id !== deletedCategory._id),
        );
        if (
          selectedCategory?._id === deletedCategory._id ||
          selectedCategory?.ancestorIds.includes(deletedCategory._id)
        )
          onSelect(undefined);
      })
      .catch(() => {});
  };

  const onUploadFolder = async (
    files: File[],
    onProgress: (data: string) => void,
  ) => {
    await uploadManyClipArts(files, selectedCategory?._id, onProgress).finally(
      () => onFolderUploaded(),
    );
  };

  const onFolderUploaded = () => {
    if (selectedCategory) {
      selectedCategory.actions?.read?.();
    } else {
      getCategoryList();
    }
  };

  const onClone = (category: ClipArtCategory) => {
    setCloningSource(category);
  };

  const onCloneConfirm = (destinationId: string | null, cloneName: string) => {
    if (!cloningSource) return;
    setCloning(true);
    clipArtCategoryApi
      .cloneOne(cloningSource._id, destinationId, cloneName)
      .then((res) => {
        const cloned = res.data;
        if (destinationId === null) {
          setCategoryList((prev) => [cloned, ...prev]);
        } else {
          const destNode = nodeRegistry.current.get(destinationId);
          if (destNode) destNode.actions?.read?.();
          else setPage(1);
        }
        setCloningSource(null);
        addToast({
          severity: "success",
          summary: "Cloned",
          detail: `"${cloned.name}" has been cloned successfully.`,
        });
      })
      .catch(() => {})
      .finally(() => setCloning(false));
  };

  const onClickOutSide = (
    event: MouseEvent<HTMLDivElement, globalThis.MouseEvent>,
  ) => {
    const treeElement = treeRef.current;
    if (!treeElement) return;
    const children = treeElement.children;
    const target = event.target;
    if (target === children[0] || target === children[1]) {
      setSelectedCategory(undefined);
      onChange?.(undefined);
    }
  };

  return (
    <div
      ref={treeRef}
      className="flex h-full w-full flex-col"
      onClick={onClickOutSide}
    >
      <div className="flex flex-col gap-3.25 border-b-1 border-gray-300 p-4">
        <div className="ml-auto flex w-fit items-center gap-2">
          <div className="mr-auto flex items-center gap-2">
            {loading && <Loader className="animate-spin" size={20} />}
          </div>
          {selectedCategory && (
            <Button
              style={{ width: 28, height: 28 }}
              severity="warning"
              tooltip="Unselect category"
              tooltipOptions={{ position: "top" }}
              icon={<FolderX size={18} />}
              onClick={() => onSelect(undefined)}
            />
          )}
          <Uploader accept="image" type="folder" handler={onUploadFolder} />
          <Button
            style={{ width: 28, height: 28 }}
            tooltip="New category"
            tooltipOptions={{ position: "top" }}
            icon={<FolderPlus size={18} />}
            onClick={() =>
              selectedCategory
                ? selectedCategory.actions?.create?.()
                : setCreating(true)
            }
          />
        </div>
        <SearchInput
          onChange={() => {
            setCategoryList([]);
            setHasMore(true);
            setLoading(true);
          }}
          onSearch={(value) => {
            setPage(1);
            setSearch(value);
          }}
        />
      </div>

      <InfiniteScroll
        loadMore={() => {
          setLoading(true);
          setPage((pre) => pre + 1);
        }}
        hasMore={hasMore}
        loading={loading}
      >
        <div className="relative flex-1 overflow-auto">
          <RootCategoryNode
            data={rootCategory}
            selectedCategory={selectedCategory}
            onSelect={onSelect}
          />
          {creating && (
            <div className="flex items-center gap-1 pl-2">
              <i className="pi pi-chevron-right" style={{ fontSize: 12 }}></i>
              <Input
                type="text"
                style={{ border: "none" }}
                autoFocus
                onBlur={(event) => onCreate(event.target.value)}
                onKeyDown={(event) => {
                  if (event.code === "Enter") {
                    event.currentTarget.blur();
                  }
                }}
              />
            </div>
          )}
          {categoryList.map((c) => (
            <CategoryNode
              key={c._id}
              data={c}
              modifiable
              disableClone={disableClone}
              selectedCategory={selectedCategory}
              onSelect={onSelect}
              onDelete={onDelete}
              onClone={onClone}
              onRegisterNode={registerNode}
              onUnregisterNode={unregisterNode}
            />
          ))}
        </div>
      </InfiniteScroll>
      {cloningSource && (
        <CloneDestDialog
          source={cloningSource}
          visible={!!cloningSource}
          loading={cloning}
          onHide={() => setCloningSource(null)}
          onConfirm={onCloneConfirm}
        />
      )}
    </div>
  );
}

export default memo(CategoryTree);
